import { useMutation, useQueryClient } from '@tanstack/react-query';
import { boxesQuery } from 'networking/mindfeed/queries/boxes';
import { isContext } from 'utils/ContextUtils';
import type { Box } from 'models/Box';
import { deleteBox } from 'networking/mindfeed/services/box.service';
import { NotificationType, notify } from 'xstate/actors/NotificationActor';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';

type Context = { previousBoxes: Box[] };

const useDeleteBox = ({
  onSuccess,
  removeOnSuccess = false,
}: {
  onSuccess?: () => void;
  removeOnSuccess?: boolean;
}) => {
  const queryClient = useQueryClient();
  const { selectedSpace } = useActiveSpace();

  const onMutateCallback = async (box: Box) => {
    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({
      queryKey: boxesQuery(selectedSpace?.id).queryKey,
    });

    // Snapshot the previous value
    const previousBoxes = queryClient.getQueryData<Box[]>(
      boxesQuery(selectedSpace?.id).queryKey,
    );

    // Optimistically update to the new value
    if (previousBoxes) {
      const updater = previousBoxes.filter((b) => {
        return b.id !== box.id;
      });

      queryClient.setQueryData<Box[]>(
        boxesQuery(selectedSpace?.id).queryKey,
        updater,
      );
    }

    return { previousBoxes };
  };

  const onSuccessCallback = async (
    _data: void,
    _variables: Box,
    _context: Context,
  ) => {
    if (removeOnSuccess) {
      queryClient.removeQueries({
        queryKey: boxesQuery(selectedSpace?.id).queryKey,
      });
    }
    if (onSuccess) {
      onSuccess();
    }

    notify(NotificationType.BOX_DELETED);
  };

  const onErrorCallback = (_err: Error, _variables: Box, context: Context) => {
    // If the mutation fails, use the context returned from onMutate to roll back
    if (isContext<Context>(context, 'previousBoxes')) {
      queryClient.setQueryData<Box[]>(
        boxesQuery(selectedSpace?.id).queryKey,
        context.previousBoxes,
      );
    }
  };

  const deleteBoxMutation = useMutation<void, Error, Box, Context>({
    mutationFn: (box) => deleteBox(box),
    onMutate: onMutateCallback,
    onSuccess: onSuccessCallback,
    onError: onErrorCallback,
  });

  return deleteBoxMutation;
};

export default useDeleteBox;
